import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import {
  extractFlattenedFieldAPI,
  isValidField as isValidFieldAPI,
  formatDateAPI,
} from "../../../../Utils/WatchlistSummary/validation";

// ===== TYPES & INTERFACES =====

interface SectionData {
  sectionScore?: number;
  sectionHealthFlag?: string;
  sectionRelevanceFlag?: string;
}

interface GrowthInitiative {
  name?: string;
  title?: string;
  initiative?: string;
  description?: string;
  details?: string;
  status?: string;
  progress?: string;
  impact?: string;
  expectedImpact?: string;
  type?: string;
  date?: string;
  "source.title"?: string;
  "source.url"?: string;
  "source.name"?: string;
  "source.date"?: string;
  source?: {
    url?: string;
    name?: string;
    date?: string;
  };
}

interface RelationshipInsight {
  entity?: string;
  description?: string;
  relevance?: string;
  impact?: string;
}

interface NewProduct {
  name?: string;
  type?: string;
  market?: string;
  launch_date?: string;
  description?: string;
  opportunity_flag?: boolean;
  "source.url"?: string;
  "source.name"?: string;
  source?: {
    url?: string;
    name?: string;
  };
}

interface HiringDepartment {
  department?: string;
  trend?: string;
  key_roles?: string;
  recent_hires?: string;
  "source.url"?: string;
  "source.name"?: string;
  source?: {
    url?: string;
    name?: string;
  };
}

interface Partnership {
  partner?: string;
  type?: string;
  announced_date?: string;
  impact?: string;
  description?: string;
  opportunity_flag?: boolean;
  "source.url"?: string;
  "source.name"?: string;
  source?: {
    url?: string;
    name?: string;
  };
}

interface GrowthSectionProps {
  data: any;
  sectionData: any;
  accountName: string;
  organizationName?: string;
  onToggleAudio?: () => void;
}

// Helper to parse CSS string to object
const parseCSSString = (cssStr: string): React.CSSProperties => {
  const styles: any = {};
  cssStr.split(";").forEach(rule => {
    const [key, value] = rule.split(":").map(s => s.trim());
    if (key && value) {
      const camelKey = key.replace(/-([a-z])/g, g => g[1].toUpperCase());
      styles[camelKey] = value;
    }
  });
  return styles;
};

// ===== SCORE BUBBLE COMPONENT =====

const GrowthScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(score);

  return (
    <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

// ===== MODAL HEADER COMPONENT =====

const GrowthModalHeader: React.FC<{
  accountName: string;
  sectionData: SectionData;
  onToggleAudio?: () => void;
}> = ({ accountName, sectionData, onToggleAudio }) => {
  const sectionScore = sectionData.sectionScore || 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <GrowthScoreBubble score={sectionScore} />
      <div>
        <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>Growth Signals Analysis</em>
        </h3>
      </div>
      <button
        id="growth-audio-btn"
        title="Play growth audio narration"
        onClick={onToggleAudio}
        style={{
          background: "none",
          border: "none",
          padding: 8,
          borderRadius: 6,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8b5cf6",
        }}
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// ===== MODAL BODY COMPONENT =====

const GrowthModalBody: React.FC<{
  synopsis?: string | null;
  confidenceScore?: number | null;
  dataFreshness?: string | null;
  lastUpdated?: string | null;
  sources?: any[];
  sectionData: SectionData;
  organizationName?: string;
}> = ({
  synopsis,
  confidenceScore,
  dataFreshness,
  sources,
  sectionData,
  organizationName,
}) => {
  const healthFlag = sectionData.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData.sectionRelevanceFlag || "N/A";

  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ")
    : null;

  const formatConfidenceScore = (score: number | null | undefined) => {
    if (score === null || score === undefined) return null;
    if (typeof score === "number") {
      const percentage =
        score <= 1 ? Math.round(score * 100) : Math.round(score);
      const color =
        percentage >= 80 ? "#16a34a" : percentage >= 50 ? "#d97706" : "#dc2626";
      return { display: `${percentage}%`, color, hasValue: true };
    }
    return null;
  };

  const confidenceData = formatConfidenceScore(confidenceScore);
  const hasValidFreshness =
    isValidFieldAPI(dataFreshness) && dataFreshness !== "N/A";
  const hasValidSources =
    Array.isArray(sources) &&
    sources.length > 0 &&
    sources.some(s => isValidFieldAPI(s?.name || s));

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  const badgeStyle = (bgColor: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    background: bgColor,
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    color: "white",
  });

  const orgDisplay = organizationName || "Organization";

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={badgeStyle(
            healthFlag === "G"
              ? "#16a34a"
              : healthFlag === "Y"
                ? "#d97706"
                : "#dc2626"
          )}
        >
          <span>Account Health:</span>
          <strong>{getHealthText(healthFlag)}</strong>
        </div>
        <div style={badgeStyle("#8b5cf6")}>
          <span>Relevance to {orgDisplay}:</span>
          <strong>{getRelevanceText(relevanceFlag)}</strong>
        </div>
      </div>

      {synopsis ? (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 12,
              marginTop: 12,
            }}
          >
            {confidenceData?.hasValue && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  padding: "6px 8px",
                  background: "#f8fafc",
                  borderRadius: 4,
                }}
              >
                <span style={{ color: "#64748b", fontWeight: 500 }}>
                  Confidence:
                </span>
                <span
                  style={{
                    color: confidenceData.color,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {confidenceData.display}
                </span>
              </div>
            )}
            {hasValidFreshness && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  padding: "6px 8px",
                  background: "#f8fafc",
                  borderRadius: 4,
                }}
              >
                <span style={{ color: "#64748b", fontWeight: 500 }}>
                  Freshness:
                </span>
                <span
                  style={{
                    color: "#1e293b",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {dataFreshness}
                </span>
              </div>
            )}
            {hasValidSources && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  padding: "6px 8px",
                  background: "#f8fafc",
                  borderRadius: 4,
                }}
              >
                <span style={{ color: "#64748b", fontWeight: 500 }}>
                  Sources:
                </span>
                <span
                  style={{
                    color: "#1e293b",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    maxWidth: 120,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {sourcesText}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <div
            style={{
              textAlign: "center",
              color: "#6b7280",
              fontStyle: "italic",
            }}
          >
            No growth summary available
          </div>
        </div>
      )}
    </div>
  );
};

// ===== ORGANIZATION RELEVANCE CARD =====

const OrganizationRelevanceCard: React.FC<{
  relationshipInsights?: RelationshipInsight[];
  organizationName?: string;
}> = ({ relationshipInsights, organizationName }) => {
  if (!Array.isArray(relationshipInsights) || relationshipInsights.length === 0)
    return null;

  const validInsights = relationshipInsights.filter(
    insight =>
      isValidFieldAPI(insight?.description) ||
      isValidFieldAPI(insight?.relevance) ||
      isValidFieldAPI(insight?.impact)
  );

  if (validInsights.length === 0) return null;

  const orgDisplay = organizationName || "Organization";

  const getRelevanceColor = (relevance?: string) => {
    const normalized = relevance?.toLowerCase();
    switch (normalized) {
      case "high":
        return "color: #16a34a; background: #f0fdf4; border: 1px solid #bbf7d0";
      case "medium":
        return "color: #d97706; background: #fef3c7; border: 1px solid #fde68a";
      case "low":
        return "color: #dc2626; background: #fef2f2; border: 1px solid #fecaca";
      default:
        return "color: #6b7280; background: #f8fafc; border: 1px solid #e5e7eb";
    }
  };

  const getImpactColor = (impact?: string) => {
    const normalized = impact?.toLowerCase();
    if (normalized?.includes("positive") || normalized?.includes("high")) {
      return "color: #16a34a; background: #f0fdf4; border: 1px solid #bbf7d0";
    } else if (
      normalized?.includes("negative") ||
      normalized?.includes("low")
    ) {
      return "color: #dc2626; background: #fef2f2; border: 1px solid #fecaca";
    } else if (
      normalized?.includes("neutral") ||
      normalized?.includes("medium")
    ) {
      return "color: #3b82f6; background: #eff6ff; border: 1px solid #bfdbfe";
    }
    return "color: #6b7280; background: #f8fafc; border: 1px solid #e5e7eb";
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🎯</span> Relevance for {orgDisplay}
        </h4>
      </div>

      <div style={{ padding: 16 }}>
        <div>
          {validInsights.map((insight, i) => {
            const relevanceColor = getRelevanceColor(insight?.relevance);
            const impactColor = getImpactColor(insight?.impact);

            return (
              <div
                key={i}
                style={{
                  padding: 12,
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  {isValidFieldAPI(insight?.relevance) && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 500,
                        ...parseStyleString(relevanceColor),
                      }}
                    >
                      {insight.relevance} relevance
                    </span>
                  )}
                  {isValidFieldAPI(insight?.impact) && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 500,
                        ...parseStyleString(impactColor),
                      }}
                    >
                      {insight.impact} impact
                    </span>
                  )}
                </div>
                {isValidFieldAPI(insight?.description) && (
                  <div
                    style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}
                  >
                    {insight.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Helper to parse style strings
const parseStyleString = (styleStr: string): React.CSSProperties => {
  const styles: any = {};
  styleStr.split(";").forEach(rule => {
    const [key, value] = rule.split(":").map(s => s.trim());
    if (key && value) {
      const camelKey = key.replace(/-([a-z])/g, g => g[1].toUpperCase());
      styles[camelKey] = value;
    }
  });
  return styles;
};

// ===== GROWTH METRICS CARD =====

const GrowthMetricsCard: React.FC<{
  growthInitiatives?: GrowthInitiative[];
  newProductsMarkets?: NewProduct[];
  hiringDepartments?: HiringDepartment[];
  keyPartnerships?: Partnership[];
}> = ({
  growthInitiatives,
  newProductsMarkets,
  hiringDepartments,
  keyPartnerships,
}) => {
  const initiativesCount = Array.isArray(growthInitiatives)
    ? growthInitiatives.filter(item => isValidFieldAPI(item)).length
    : 0;
  const productsCount = Array.isArray(newProductsMarkets)
    ? newProductsMarkets.filter(item => isValidFieldAPI(item)).length
    : 0;
  const hiringCount = Array.isArray(hiringDepartments)
    ? hiringDepartments.filter(item => isValidFieldAPI(item)).length
    : 0;
  const partnershipsCount = Array.isArray(keyPartnerships)
    ? keyPartnerships.filter(item => isValidFieldAPI(item)).length
    : 0;

  const totalSignals =
    initiativesCount + productsCount + hiringCount + partnershipsCount;

  if (totalSignals === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📊</span> Key Metrics
        </h4>
      </div>

      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: 12,
          }}
        >
          <div
            style={{
              textAlign: "center",
              padding: 12,
              background: "#f0f9ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 700, color: "#1e40af" }}>
              {totalSignals}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
              Total Signals
            </div>
          </div>
          {productsCount > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: "#16a34a" }}>
                {productsCount}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                New Products
              </div>
            </div>
          )}
          {hiringCount > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#fef3c7",
                border: "1px solid #fde68a",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: "#d97706" }}>
                {hiringCount}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                Hiring Depts
              </div>
            </div>
          )}
          {partnershipsCount > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#f3e8ff",
                border: "1px solid #d8b4fe",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: "#8b5cf6" }}>
                {partnershipsCount}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                Partnerships
              </div>
            </div>
          )}
          {initiativesCount > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 700, color: "#dc2626" }}>
                {initiativesCount}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                Initiatives
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface GrowthInitiative {
  name?: string;
  title?: string;
  description?: string;
  status?: string;
  impact?: string;
  type?: string;
  date?: string;
  "source.title"?: string;
  "source.url"?: string;
  "source.name"?: string;
  "source.date"?: string;
  source?: { url?: string; name?: string; date?: string };
}

interface RelationshipInsight {
  entity?: string;
  description?: string;
  relevance?: string;
  impact?: string;
}

const getStatusColor = (status?: string): string => {
  const normalized = status?.toLowerCase();
  if (normalized?.includes("active") || normalized?.includes("ongoing"))
    return "background: #16a34a; color: white";
  if (normalized?.includes("planned") || normalized?.includes("pending"))
    return "background: #d97706; color: white";
  if (normalized?.includes("completed"))
    return "background: #3b82f6; color: white";
  return "background: #6b7280; color: white";
};

const getImpactBadgeColor = (impact?: string): string => {
  const normalized = impact?.toLowerCase();
  if (normalized?.includes("high") || normalized?.includes("positive"))
    return "background: #16a34a; color: white";
  if (normalized?.includes("medium") || normalized?.includes("moderate"))
    return "background: #d97706; color: white";
  if (normalized?.includes("low") || normalized?.includes("negative"))
    return "background: #dc2626; color: white";
  return "background: #6b7280; color: white";
};

const getRelevanceColor = (relevance?: string): string => {
  const normalized = relevance?.toLowerCase();
  switch (normalized) {
    case "high":
      return "color: #16a34a; background: #f0fdf4; border: 1px solid #bbf7d0";
    case "medium":
      return "color: #d97706; background: #fef3c7; border: 1px solid #fde68a";
    case "low":
      return "color: #dc2626; background: #fef2f2; border: 1px solid #fecaca";
    default:
      return "color: #6b7280; background: #f8fafc; border: 1px solid #e5e7eb";
  }
};

const getImpactColor = (impact?: string): string => {
  const normalized = impact?.toLowerCase();
  if (normalized?.includes("positive") || normalized?.includes("high")) {
    return "color: #16a34a; background: #f0fdf4; border: 1px solid #bbf7d0";
  } else if (normalized?.includes("negative") || normalized?.includes("low")) {
    return "color: #dc2626; background: #fef2f2; border: 1px solid #fecaca";
  } else if (
    normalized?.includes("neutral") ||
    normalized?.includes("medium")
  ) {
    return "color: #3b82f6; background: #eff6ff; border: 1px solid #bfdbfe";
  }
  return "color: #6b7280; background: #f8fafc; border: 1px solid #e5e7eb";
};

export const InitiativesSection: React.FC<{
  growthInitiatives: GrowthInitiative[];
  relationshipInsights: RelationshipInsight[];
  organizationName?: string;
}> = ({ growthInitiatives, relationshipInsights }) => {
  const hasInitiatives =
    Array.isArray(growthInitiatives) && growthInitiatives.length > 0;
  const hasRelationshipInsights =
    Array.isArray(relationshipInsights) && relationshipInsights.length > 0;

  if (!hasInitiatives && !hasRelationshipInsights) return null;

  const validInitiatives = hasInitiatives
    ? growthInitiatives.filter(
        initiative =>
          isValidFieldAPI(initiative?.name) ||
          isValidFieldAPI(initiative?.description) ||
          isValidFieldAPI(initiative?.["source.title"])
      )
    : [];

  const validInsights = hasRelationshipInsights
    ? relationshipInsights.filter(
        insight =>
          isValidFieldAPI(insight?.entity) ||
          isValidFieldAPI(insight?.description)
      )
    : [];

  if (validInitiatives.length === 0 && validInsights.length === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🚀</span> Initiatives
        </h4>
      </div>

      <div style={{ padding: 16 }}>
        {validInitiatives.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>📈</span> Growth Initiatives
            </h5>
            <div>
              {validInitiatives.map((initiative, i) => {
                const title =
                  initiative?.["source.title"] ||
                  initiative?.title ||
                  initiative?.name ||
                  `Initiative ${i + 1}`;
                const description = initiative?.description || "";
                const status = initiative?.status || "";
                const impact = initiative?.impact || "";
                const type = initiative?.type || "";
                const date = formatDateAPI(initiative?.date);
                const sourceUrl =
                  initiative?.["source.url"] || initiative?.source?.url;
                const sourceName =
                  initiative?.["source.name"] ||
                  initiative?.source?.name ||
                  "Source";
                const sourceDate = formatDateAPI(
                  initiative?.["source.date"] || initiative?.source?.date
                );

                return (
                  <div
                    key={i}
                    style={{
                      padding: 16,
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        marginBottom: 12,
                      }}
                    >
                      <h6
                        style={{
                          margin: 0,
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#1e293b",
                          flex: 1,
                        }}
                      >
                        {title}
                      </h6>
                      <div style={{ display: "flex", gap: 6 }}>
                        {isValidFieldAPI(status) && (
                          <span
                            style={{
                              padding: "3px 8px",
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 500,
                              ...parseCSSString(getStatusColor(status)),
                            }}
                          >
                            {status}
                          </span>
                        )}
                        {isValidFieldAPI(impact) && (
                          <span
                            style={{
                              padding: "3px 8px",
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 500,
                              ...parseCSSString(getImpactBadgeColor(impact)),
                            }}
                          >
                            {impact} IMPACT
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                        marginBottom: 12,
                        fontSize: 11,
                      }}
                    >
                      {isValidFieldAPI(type) && (
                        <div>
                          <strong>Type:</strong> {type}
                        </div>
                      )}
                      {date && (
                        <div>
                          <strong>Date:</strong> {date}
                        </div>
                      )}
                    </div>

                    {isValidFieldAPI(description) && (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#374151",
                          lineHeight: 1.5,
                          marginBottom: 12,
                        }}
                      >
                        {description}
                      </div>
                    )}

                    {(sourceUrl || sourceName) && (
                      <div
                        style={{
                          paddingTop: 8,
                          borderTop: "1px solid #e5e7eb",
                          fontSize: 11,
                          color: "#64748b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            {sourceUrl ? (
                              <a
                                href={sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#3b82f6",
                                  textDecoration: "none",
                                  fontWeight: 500,
                                }}
                              >
                                📰 {sourceName}
                              </a>
                            ) : (
                              <span>📰 {sourceName}</span>
                            )}
                          </div>
                          {sourceDate && <div>{sourceDate}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {validInsights.length > 0 && (
          <div style={{ marginBottom: 0 }}>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>🔍</span> Relationship Insights
            </h5>
            <div>
              {validInsights.map((insight, i) => {
                const relevanceColor = getRelevanceColor(insight?.relevance);
                const impactColor = getImpactColor(insight?.impact);

                return (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 12,
                    }}
                  >
                    {isValidFieldAPI(insight?.entity) && (
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1e293b",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span>🤝</span> {insight.entity}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      {isValidFieldAPI(insight?.relevance) && (
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 500,
                            ...parseCSSString(relevanceColor),
                          }}
                        >
                          {insight.relevance} relevance
                        </span>
                      )}
                      {isValidFieldAPI(insight?.impact) && (
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 500,
                            ...parseCSSString(impactColor),
                          }}
                        >
                          {insight.impact} impact
                        </span>
                      )}
                    </div>
                    {isValidFieldAPI(insight?.description) && (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#374151",
                          lineHeight: 1.5,
                        }}
                      >
                        {insight.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface Trend {
  category: string;
  trend?: string | null;
  change?: string | null;
  period?: string | null;
  description?: string;
  icon: string;
}

interface NewProduct {
  name?: string;
  type?: string;
  market?: string;
  launch_date?: string;
  description?: string;
  opportunity_flag?: boolean;
  "source.url"?: string;
  "source.name"?: string;
  source?: { url?: string; name?: string };
}

interface HiringDepartment {
  department?: string;
  trend?: string;
  key_roles?: string;
  recent_hires?: string;
  "source.url"?: string;
  "source.name"?: string;
  source?: { url?: string; name?: string };
}

interface Partnership {
  partner?: string;
  type?: string;
  announced_date?: string;
  impact?: string;
  description?: string;
  opportunity_flag?: boolean;
  "source.url"?: string;
  "source.name"?: string;
  source?: { url?: string; name?: string };
}

const getTrendColor = (trend?: string | null): string => {
  const normalized = trend?.toLowerCase();
  if (normalized === "increasing" || normalized === "growth")
    return "color: #16a34a";
  if (normalized === "decreasing" || normalized === "decline")
    return "color: #dc2626";
  if (normalized === "stable") return "color: #d97706";
  return "color: #64748b";
};

const getTrendBackgroundColor = (trend?: string | null): string => {
  const normalized = trend?.toLowerCase();
  if (normalized === "increasing" || normalized === "growth")
    return "background: #f0fdf4; border: 1px solid #bbf7d0";
  if (normalized === "decreasing" || normalized === "decline")
    return "background: #fef2f2; border: 1px solid #fecaca";
  if (normalized === "stable")
    return "background: #fef3c7; border: 1px solid #fde68a";
  return "background: #f8fafc; border: 1px solid #e5e7eb";
};

const getTrendIcon = (trend?: string | null): string => {
  const normalized = trend?.toLowerCase();
  if (normalized === "increasing" || normalized === "growth") return "📈";
  if (normalized === "decreasing" || normalized === "decline") return "📉";
  if (normalized === "stable") return "➡️";
  return "📊";
};

// Sub-component for New Products
const NewProductsSubSection: React.FC<{ products: NewProduct[] }> = ({
  products,
}) => (
  <div style={{ marginBottom: 24 }}>
    <h5
      style={{
        margin: "0 0 12px 0",
        fontSize: 14,
        fontWeight: 600,
        color: "#374151",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>🆕</span> New Products & Markets
    </h5>
    <div>
      {products.map((product, i) => {
        const sourceUrl = product["source.url"] || product.source?.url;
        const sourceName =
          product["source.name"] || product.source?.name || "Source";

        return (
          <div
            key={i}
            style={{
              padding: 12,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600, color: "#166534", marginBottom: 8 }}>
              {product.name || "New Product"}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 8,
                fontSize: 12,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              {product.type && (
                <div>
                  <strong>Type:</strong> {product.type}
                </div>
              )}
              {product.market && (
                <div>
                  <strong>Market:</strong> {product.market}
                </div>
              )}
              {product.launch_date && (
                <div>
                  <strong>Launch:</strong> {formatDateAPI(product.launch_date)}
                </div>
              )}
            </div>
            {product.description && (
              <div
                style={{
                  fontSize: 13,
                  color: "#374151",
                  lineHeight: 1.5,
                  marginBottom: 8,
                }}
              >
                {product.description}
              </div>
            )}
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 11,
                  color: "#3b82f6",
                  textDecoration: "none",
                }}
              >
                📰 {sourceName}
              </a>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// Sub-component for Hiring
const HiringSubSection: React.FC<{ departments: HiringDepartment[] }> = ({
  departments,
}) => (
  <div style={{ marginBottom: 24 }}>
    <h5
      style={{
        margin: "0 0 12px 0",
        fontSize: 14,
        fontWeight: 600,
        color: "#374151",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>👥</span> Hiring Trends
    </h5>
    <div>
      {departments.map((dept, i) => {
        const sourceUrl = dept["source.url"] || dept.source?.url;
        const sourceName = dept["source.name"] || dept.source?.name || "Source";

        return (
          <div
            key={i}
            style={{
              padding: 12,
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600, color: "#92400e", marginBottom: 8 }}>
              {dept.department || "Department"}
            </div>
            {dept.trend && (
              <div style={{ fontSize: 12, color: "#374151", marginBottom: 4 }}>
                <strong>Trend:</strong> {dept.trend}
              </div>
            )}
            {dept.key_roles && (
              <div style={{ fontSize: 12, color: "#374151", marginBottom: 4 }}>
                <strong>Key Roles:</strong> {dept.key_roles}
              </div>
            )}
            {dept.recent_hires && (
              <div style={{ fontSize: 12, color: "#374151", marginBottom: 8 }}>
                <strong>Recent Hires:</strong> {dept.recent_hires}
              </div>
            )}
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 11,
                  color: "#3b82f6",
                  textDecoration: "none",
                }}
              >
                📰 {sourceName}
              </a>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// Sub-component for Partnerships
const PartnershipsSubSection: React.FC<{ partnerships: Partnership[] }> = ({
  partnerships,
}) => (
  <div>
    <h5
      style={{
        margin: "0 0 12px 0",
        fontSize: 14,
        fontWeight: 600,
        color: "#374151",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>🤝</span> Key Partnerships
    </h5>
    <div>
      {partnerships.map((partnership, i) => {
        const sourceUrl = partnership["source.url"] || partnership.source?.url;
        const sourceName =
          partnership["source.name"] || partnership.source?.name || "Source";

        return (
          <div
            key={i}
            style={{
              padding: 12,
              background: "#f3e8ff",
              border: "1px solid #d8b4fe",
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600, color: "#6b21a8", marginBottom: 8 }}>
              {partnership.partner || "Partner"}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 8,
                fontSize: 12,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              {partnership.type && (
                <div>
                  <strong>Type:</strong> {partnership.type}
                </div>
              )}
              {partnership.announced_date && (
                <div>
                  <strong>Announced:</strong>{" "}
                  {formatDateAPI(partnership.announced_date)}
                </div>
              )}
              {partnership.impact && (
                <div>
                  <strong>Impact:</strong> {partnership.impact}
                </div>
              )}
            </div>
            {partnership.description && (
              <div
                style={{
                  fontSize: 13,
                  color: "#374151",
                  lineHeight: 1.5,
                  marginBottom: 8,
                }}
              >
                {partnership.description}
              </div>
            )}
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 11,
                  color: "#3b82f6",
                  textDecoration: "none",
                }}
              >
                📰 {sourceName}
              </a>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export const GrowthTrendsWithMetrics: React.FC<{
  data: any;
  newProductsMarkets: NewProduct[];
  hiringDepartments: HiringDepartment[];
  keyPartnerships: Partnership[];
}> = ({ data, newProductsMarkets, hiringDepartments, keyPartnerships }) => {
  // Extract trend metrics
  const newProductsTrend = extractFlattenedFieldAPI(
    data,
    "newProductGrowthSignals.trend",
    null
  );
  const newProductsChange = extractFlattenedFieldAPI(
    data,
    "newProductGrowthSignals.change",
    null
  );
  const newProductsPeriod = extractFlattenedFieldAPI(
    data,
    "newProductGrowthSignals.period",
    null
  );
  const newProductsCurrentCount = extractFlattenedFieldAPI(
    data,
    "newProductGrowthSignals.currentCount",
    null
  );
  const newProductsPreviousCount = extractFlattenedFieldAPI(
    data,
    "newProductGrowthSignals.previousCount",
    null
  );

  const hiringTrend = extractFlattenedFieldAPI(
    data,
    "hiringTrendGrowthSignals.trend",
    null
  );
  const hiringChange = extractFlattenedFieldAPI(
    data,
    "hiringTrendGrowthSignals.change",
    null
  );
  const hiringPeriod = extractFlattenedFieldAPI(
    data,
    "hiringTrendGrowthSignals.period",
    null
  );
  const hiringDescription = extractFlattenedFieldAPI(
    data,
    "hiringTrendGrowthSignals.description",
    null
  );
  const hiringCurrentCount = extractFlattenedFieldAPI(
    data,
    "hiringTrendGrowthSignals.currentCount",
    null
  );
  const hiringPreviousCount = extractFlattenedFieldAPI(
    data,
    "hiringTrendGrowthSignals.previousCount",
    null
  );

  const partnershipTrend = extractFlattenedFieldAPI(
    data,
    "partnershipGrowthSignals.trend",
    null
  );
  const partnershipChange = extractFlattenedFieldAPI(
    data,
    "partnershipGrowthSignals.change",
    null
  );
  const partnershipPeriod = extractFlattenedFieldAPI(
    data,
    "partnershipGrowthSignals.period",
    null
  );
  const partnershipCurrentCount = extractFlattenedFieldAPI(
    data,
    "partnershipGrowthSignals.currentCount",
    null
  );
  const partnershipPreviousCount = extractFlattenedFieldAPI(
    data,
    "partnershipGrowthSignals.previousCount",
    null
  );

  // Build trends array
  const trends: Trend[] = [];

  if (
    isValidFieldAPI(newProductsTrend) ||
    isValidFieldAPI(newProductsChange) ||
    (Array.isArray(newProductsMarkets) && newProductsMarkets.length > 0)
  ) {
    trends.push({
      category: "New Products",
      trend: newProductsTrend,
      change: newProductsChange,
      period: newProductsPeriod,
      description: `${newProductsCurrentCount || 0} current vs ${newProductsPreviousCount || 0} previous`,
      icon: "🆕",
    });
  }

  if (
    isValidFieldAPI(hiringTrend) ||
    isValidFieldAPI(hiringChange) ||
    (Array.isArray(hiringDepartments) && hiringDepartments.length > 0)
  ) {
    trends.push({
      category: "Hiring",
      trend: hiringTrend,
      change: hiringChange,
      period: hiringPeriod,
      description:
        hiringDescription ||
        `${hiringCurrentCount || 0} current vs ${hiringPreviousCount || 0} previous`,
      icon: "👥",
    });
  }

  if (
    isValidFieldAPI(partnershipTrend) ||
    isValidFieldAPI(partnershipChange) ||
    (Array.isArray(keyPartnerships) && keyPartnerships.length > 0)
  ) {
    trends.push({
      category: "Partnerships",
      trend: partnershipTrend,
      change: partnershipChange,
      period: partnershipPeriod,
      description: `${partnershipCurrentCount || 0} current vs ${partnershipPreviousCount || 0} previous`,
      icon: "🤝",
    });
  }

  const hasNewProducts =
    Array.isArray(newProductsMarkets) && newProductsMarkets.length > 0;
  const hasHiring =
    Array.isArray(hiringDepartments) && hiringDepartments.length > 0;
  const hasPartnerships =
    Array.isArray(keyPartnerships) && keyPartnerships.length > 0;

  if (trends.length === 0 && !hasNewProducts && !hasHiring && !hasPartnerships)
    return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📈</span> Growth Trends
        </h4>
      </div>

      <div style={{ padding: 16 }}>
        {trends.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>📊</span> Trend Metrics
            </h5>
            <div>
              {trends.map((trend, i) => {
                const trendColor = getTrendColor(trend.trend);
                const bgColor = getTrendBackgroundColor(trend.trend);

                return (
                  <div
                    key={i}
                    style={{
                      padding: 16,
                      ...parseCSSString(bgColor),
                      borderRadius: 8,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{trend.icon}</span>
                        <strong style={{ color: "#374151", fontSize: 14 }}>
                          {trend.category}
                        </strong>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {isValidFieldAPI(trend.trend) && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              ...parseCSSString(trendColor),
                              textTransform: "uppercase",
                            }}
                          >
                            {getTrendIcon(trend.trend)} {trend.trend}
                          </span>
                        )}
                        {isValidFieldAPI(trend.change) && (
                          <span style={{ fontSize: 12, color: "#64748b" }}>
                            {trend.change}
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 12,
                        fontSize: 12,
                      }}
                    >
                      {isValidFieldAPI(trend.description) && (
                        <div style={{ color: "#374151" }}>
                          {trend.description}
                        </div>
                      )}
                      {isValidFieldAPI(trend.period) && (
                        <div style={{ color: "#64748b", textAlign: "right" }}>
                          {trend.period}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hasNewProducts && (
          <NewProductsSubSection products={newProductsMarkets} />
        )}
        {hasHiring && <HiringSubSection departments={hiringDepartments} />}
        {hasPartnerships && (
          <PartnershipsSubSection partnerships={keyPartnerships} />
        )}
      </div>
    </div>
  );
};

// ===== MAIN GROWTH SECTION COMPONENT =====

export const GrowthSection: React.FC<GrowthSectionProps> = ({
  data,
  sectionData,
  accountName,
  organizationName,
  onToggleAudio,
}) => {
  if (!data) return <div>No growth data available</div>;

  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const lastUpdated = extractFlattenedFieldAPI(
    data,
    "metadata.last_updated",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  const growthInitiatives = extractFlattenedFieldAPI(
    data,
    "growthInitiatives",
    []
  );
  const newProductsMarkets = extractFlattenedFieldAPI(
    data,
    "newProductsMarkets",
    []
  );
  const hiringDepartments = extractFlattenedFieldAPI(
    data,
    "hiringDepartments",
    []
  );
  const keyPartnerships = extractFlattenedFieldAPI(data, "keyPartnerships", []);
  const relationshipInsights = extractFlattenedFieldAPI(
    data,
    "relationshipInsights",
    []
  );

  return (
    <div className="growth-section-consolidated">
      <GrowthModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={onToggleAudio}
      />
      <GrowthModalBody
        synopsis={synopsis}
        confidenceScore={confidenceScore}
        dataFreshness={dataFreshness}
        lastUpdated={lastUpdated}
        sources={sources}
        sectionData={sectionData}
        organizationName={organizationName}
      />
      <OrganizationRelevanceCard
        relationshipInsights={relationshipInsights}
        organizationName={organizationName}
      />
      <GrowthMetricsCard
        growthInitiatives={growthInitiatives}
        newProductsMarkets={newProductsMarkets}
        hiringDepartments={hiringDepartments}
        keyPartnerships={keyPartnerships}
      />
      <GrowthTrendsWithMetrics
        data={data}
        newProductsMarkets={newProductsMarkets}
        hiringDepartments={hiringDepartments}
        keyPartnerships={keyPartnerships}
      />

      <InitiativesSection
        growthInitiatives={growthInitiatives}
        relationshipInsights={relationshipInsights}
        organizationName={organizationName}
      />
      {/* Add other cards as needed based on file */}
    </div>
  );
};
